/**
 * Search screen state — the query box, the debounced ``GET /search`` (sorted by the customer's saved default
 * address when there is one), the Products / Shops tab and the Food-track gating. Moved verbatim out of
 * SearchPage so the classic view and the Blinkit view run the exact same logic.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../../lib/api";
import { isFoodShop } from "../../../lib/foodCategory";

export function useSearchController() {
    const [params, setParams] = useSearchParams();
    const q = params.get("q") || "";
    // When the user lands here from the Food landing page we get
    // ``?vertical=food`` — filter shops/products to the Food family.
    const vertical = params.get("vertical") || "";
    const [tab, setTab] = useState("products"); // 'products' | 'shops'
    const [results, setResults] = useState({ restaurants: [], products: [] });
    const [input, setInput] = useState(q);
    const [loading, setLoading] = useState(false);
    const [coords, setCoords] = useState(null);
    const [verticals, setVerticals] = useState([]);

    useEffect(() => {
        if (vertical !== "food") return;
        api.get("/business-categories")
            .then(({ data }) => setVerticals(data || []))
            .catch(() => setVerticals([]));
    }, [vertical]);

    useEffect(() => {
        // iter91 (App Store / Play Store / UX): do NOT auto-request GPS
        // permission on every SearchPage mount — that popped the iOS
        // location dialog while the user was typing a query (user-
        // reported screenshot, Feb 2026). Apple Guideline 5.1.5 also
        // expects a clear user-intent gesture before a location prompt.
        //
        // Instead, use coordinates from the customer's SAVED default
        // address — that's an explicit, prior consent signal — and fall
        // back to a no-coord search if there isn't one. We never call
        // ``navigator.geolocation`` from this page; users who want
        // GPS-based search go through the address picker which has its
        // own in-context "Use current location" button.
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/addresses");
                if (cancelled) return;
                const def = (data || []).find((a) => a.is_default) || (data || [])[0];
                if (def && def.lat != null && def.lng != null) {
                    setCoords({ lat: def.lat, lng: def.lng });
                }
            } catch { /* anonymous or token expired — search runs without coords */ }
        })();
        return () => { cancelled = true; };
    }, []);

    const run = useCallback(async (query) => {
        setLoading(true);
        try {
            const u = new URLSearchParams();
            if (query) u.set("q", query);
            if (coords) { u.set("lat", coords.lat); u.set("lng", coords.lng); }
            const { data } = await api.get(`/search?${u.toString()}`);
            setResults({ restaurants: data.restaurants || [], products: data.products || [] });
        } catch { setResults({ restaurants: [], products: [] }); }
        finally { setLoading(false); }
    }, [coords]);

    useEffect(() => {
        // Debounce input changes by 250ms; fire immediately on first mount
        // so the page always shows results in the first view (no "tap to
        // search" UX where the user is greeted with empty Products/Shops
        // counts and a blank panel below).
        const t = setTimeout(() => { run(input); }, input ? 250 : 0);
        return () => clearTimeout(t);
    }, [input, coords, run]);

    const submit = (e) => {
        e.preventDefault();
        // Preserve the active vertical track when the user re-submits.
        const next = {};
        if (input) next.q = input;
        if (vertical) next.vertical = vertical;
        setParams(next);
        run(input);
    };

    // Strict vertical gating — keeps the Food search free of services / pharmacy
    // / real-estate results once we know the user is on the ``/food`` track.
    const { gatedRestaurants, gatedProducts } = useMemo(() => {
        if (vertical !== "food") {
            return { gatedRestaurants: results.restaurants, gatedProducts: results.products };
        }
        const foodShops = (results.restaurants || []).filter((r) => isFoodShop(r, verticals));
        const allowedIds = new Set(foodShops.map((r) => r.id));
        const foodProducts = (results.products || []).filter((p) => allowedIds.has(p.restaurant_id));
        return { gatedRestaurants: foodShops, gatedProducts: foodProducts };
    }, [results, vertical, verticals]);

    const counts = { products: gatedProducts.length, shops: gatedRestaurants.length };

    return {
        q, vertical, tab, setTab, results, input, setInput, loading, coords, verticals,
        run, submit, gatedRestaurants, gatedProducts, counts,
    };
}
